import type {
  Criticality,
  EngagementRequirement,
  RequirementEvidence,
} from '@/lib/api/engagement-requirements';
import type { LibraryItem, LibraryTree } from '@/lib/api/requirement-library';
import type { SourceCiteKind } from '@/components/ui/citation-chip';
import {
  formatDate as formatDay,
  matchesSearch,
  searchTerms,
} from '@/components/requirement-library/library-logic';

/**
 * Pure logic behind the engagement requirements screen (T-0142) and the
 * add-from-library picker (T-0143): filtering, search, counts, the header
 * summary, citation reading, and the picker's tri-state and running
 * totals. No React and no fetch, so it is unit tested directly.
 *
 * Behavioural reference: `reqCompute`, `citation`, `togglePick`,
 * `triState` and `pickSummary` in
 * `docs/requirement-library/prototype/selection.js`.
 */

// ---------------------------------------------------------------------------
// Criticality
// ---------------------------------------------------------------------------

export const CRITICALITY_LABEL: Record<Criticality, string> = {
  not_set: 'Not set',
  critical: 'Critical',
  desired: 'Desired',
  not_required: 'Not required',
  not_applicable: 'Not applicable',
};

/** The four values a consultant can choose; "Not set" is only a starting state. */
export const CHOOSABLE_CRITICALITIES: ReadonlyArray<Exclude<Criticality, 'not_set'>> = [
  'critical',
  'desired',
  'not_required',
  'not_applicable',
];

export function asCriticality(value: string): Criticality {
  return value in CRITICALITY_LABEL ? (value as Criticality) : 'not_set';
}

// ---------------------------------------------------------------------------
// Citation
// ---------------------------------------------------------------------------

export type RequirementCitation =
  | {
      kind: 'library';
      libraryNumber: string;
      moduleVersion: number | null;
      /** Where the library item itself came from. */
      librarySourceKind: string | null;
      libraryV1Number: string | null;
      /** The client spreadsheet row it was matched from (T-0263), or null. */
      matchedFrom: {
        file: string;
        sheet: string | null;
        row: number | string | null;
        clientRef: string | null;
      } | null;
      /** The match judge's verdict and the item's meaning score, recorded at insert (24 Oct 2026). */
      matchVerdict: string | null;
      matchScore: number | null;
    }
  | { kind: 'manual'; reason: string; supportingDocumentId: string | null }
  | {
      kind: 'spreadsheet';
      fileName: string;
      sheet: string | null;
      row: number | string | null;
      clientRef: string | null;
    }
  /** A client-specific requirement added from a discovery suggestion. */
  | { kind: 'discovery'; evidenceNumbers: string[] }
  | { kind: 'other'; rawKind: string };

function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function obj(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

/** Read a row's citation JSON into the shape the screen renders. */
export function citationOf(row: Pick<EngagementRequirement, 'citation' | 'number'>) {
  const c = obj(row.citation);
  const kind = str(c['kind']) ?? 'unknown';
  if (kind === 'library') {
    const source = obj(c['library_source']);
    const ref = obj(source['source_ref']);
    const version = c['module_version'];
    const file = str(c['file']);
    const rowNo = c['row'];
    return {
      kind,
      libraryNumber: str(c['library_number']) ?? row.number,
      moduleVersion: typeof version === 'number' ? version : null,
      librarySourceKind: str(source['source_kind']),
      libraryV1Number: str(ref['v1_number']),
      matchedFrom: file
        ? {
            file,
            sheet: str(c['sheet']),
            row: typeof rowNo === 'number' || typeof rowNo === 'string' ? rowNo : null,
            clientRef: str(c['client_ref']),
          }
        : null,
      matchVerdict: str(c['match_verdict']),
      matchScore: typeof c['match_score'] === 'number' ? c['match_score'] : null,
    } satisfies RequirementCitation;
  }
  if (kind === 'manual') {
    return {
      kind,
      reason: str(c['reason']) ?? '',
      supportingDocumentId: str(c['supporting_document_id']),
    } satisfies RequirementCitation;
  }
  if (kind === 'spreadsheet') {
    const rowNo = c['row'];
    return {
      kind,
      fileName: str(c['file_name']) ?? '',
      sheet: str(c['sheet']),
      row: typeof rowNo === 'number' || typeof rowNo === 'string' ? rowNo : null,
      clientRef: str(c['client_ref']),
    } satisfies RequirementCitation;
  }
  if (kind === 'discovery') {
    const numbers = c['evidence_numbers'];
    return {
      kind,
      evidenceNumbers: Array.isArray(numbers)
        ? numbers.filter((n): n is string => typeof n === 'string')
        : [],
    } satisfies RequirementCitation;
  }
  return { kind: 'other', rawKind: kind } satisfies RequirementCitation;
}

/**
 * Added by "Add to requirements" in Discovery (E12-F10 §3): a client-specific
 * need (citation kind `discovery`) or a library match, which keeps its
 * library citation and is told apart by the discovery load that brought it.
 */
export function addedFromDiscovery(
  row: Pick<EngagementRequirement, 'citation' | 'number' | 'load'>,
): boolean {
  return citationOf(row).kind === 'discovery' || row.load?.kind === 'discovery';
}

/**
 * The "Discovery" source filter, as the list and export endpoints read
 * `source_kind=discovery`: added from discovery, or citing discovery
 * evidence (a requirement a suggestion was linked to).
 */
export function citesDiscovery(
  row: Pick<EngagementRequirement, 'citation' | 'number' | 'load' | 'discovery_evidence'>,
): boolean {
  return addedFromDiscovery(row) || (row.discovery_evidence?.length ?? 0) > 0;
}

/** One line saying where a library item came from, for the citation block. */
export function librarySourceLabel(c: Extract<RequirementCitation, { kind: 'library' }>): string {
  switch (c.librarySourceKind) {
    case 'caliber_v1':
      return c.libraryV1Number ? `Caliber v1 catalog, ${c.libraryV1Number}` : 'Caliber v1 catalog';
    case 'spreadsheet':
      return 'Spreadsheet imported into the library';
    case 'ai_document':
      return 'Source document, accepted by a curator';
    case 'curator':
      return 'Written by a curator';
    default:
      return 'Not recorded';
  }
}

/** Short label and detail for the table's Citation column. */
export function citationSummary(row: EngagementRequirement): { label: string; detail: string } {
  const c = citationOf(row);
  if (addedFromDiscovery(row)) {
    if (c.kind === 'library') {
      return {
        label: 'Discovery',
        detail: `${c.libraryNumber}${c.moduleVersion != null ? ` v${c.moduleVersion}` : ''}`,
      };
    }
    const numbers = row.discovery_evidence?.length
      ? row.discovery_evidence.map((e) => e.number).filter((n): n is string => Boolean(n))
      : c.kind === 'discovery'
        ? c.evidenceNumbers
        : [];
    return { label: 'Discovery', detail: numbers.join(', ') };
  }
  switch (c.kind) {
    case 'library':
      return { label: 'Library', detail: c.moduleVersion != null ? `v${c.moduleVersion}` : '' };
    case 'manual':
      return { label: 'Manual', detail: c.reason };
    case 'spreadsheet':
      return {
        label: 'Imported',
        detail: [c.clientRef, c.row != null ? `row ${c.row}` : null].filter(Boolean).join(', '),
      };
    default:
      return { label: 'Other', detail: '' };
  }
}

const SOURCE_CITE_KINDS: ReadonlyArray<SourceCiteKind> = [
  'interview',
  'questionnaire',
  'follow_up',
  'document',
  'data',
];

/** One cited item, laid out for the side panel's evidence row. */
export interface EvidenceLine {
  number: string | null;
  /** Who said it; null for a document, which the citation already names. */
  who: string | null;
  /** Where, for the citation pill: `S22 Payroll Manager` at `00:04:31`, a document page. */
  where: { kind: SourceCiteKind; label: string; location: string | null } | null;
  /** "15 Oct 2026": the interview's date, else the day it was confirmed. */
  date: string;
  withdrawn: boolean;
}

export function evidenceLine(e: RequirementEvidence): EvidenceLine {
  const who = e.source && e.source.kind !== 'document' ? e.source.name || null : null;
  const cited = e.citation?.kind ?? e.channel ?? '';
  const kind = (SOURCE_CITE_KINDS as readonly string[]).includes(cited)
    ? (cited as SourceCiteKind)
    : e.interview
      ? 'interview'
      : 'document';
  let where: EvidenceLine['where'] = null;
  if (e.interview) {
    where = {
      kind,
      label: [e.interview.code, e.interview.title].filter(Boolean).join(' '),
      location: e.citation?.location || null,
    };
  } else if (e.document) {
    where = {
      kind,
      label: e.document.title,
      location: e.document.page != null ? `page ${e.document.page}` : e.citation?.location || null,
    };
  } else if (e.citation) {
    // A questionnaire answer is located by who answered: say the name once.
    const location =
      e.citation.location && e.citation.location !== who ? e.citation.location : null;
    where = { kind, label: e.citation.label, location };
  }
  return {
    number: e.number,
    who,
    where,
    date: formatDay(e.date),
    withdrawn: e.status === 'withdrawn',
  };
}

/** "E-012 · Kim Torres · S22 Payroll Manager · 15 Oct 2026". */
export function evidenceByline(e: RequirementEvidence): string {
  const l = evidenceLine(e);
  return [l.number, l.who, l.where?.label, l.date].filter(Boolean).join(' · ');
}

/** "Withdrawn on 17 Oct 2026: “Misheard”. It stays cited and no longer counts." */
export function evidenceWithdrawnText(
  e: Pick<RequirementEvidence, 'withdraw_reason' | 'withdrawn_at'>,
): string {
  const on = e.withdrawn_at ? ` on ${formatDay(e.withdrawn_at)}` : '';
  const why = (e.withdraw_reason ?? '').replace(/[.\s]+$/, '');
  return `Withdrawn${on}${why ? `: “${why}”` : ''}. It stays cited and no longer counts.`;
}

// ---------------------------------------------------------------------------
// Filters, search, counts
// ---------------------------------------------------------------------------

export type CriticalityFilter = 'all' | Criticality;
export type SourceFilter = 'any' | 'library' | 'spreadsheet' | 'manual' | 'discovery' | 'edited';

/** Rail id of the "Removed" view. Never a module id (those are uuids). */
export const REMOVED_VIEW = 'removed';

/**
 * Rail id of the "Not mapped" view: requirements with no library module
 * (ADR-025 decision 2, 24 October 2026). Also the list and export filter
 * value (`module_id=not_mapped`). Never a module id.
 */
export const NOT_MAPPED_VIEW = 'not_mapped';
export const NOT_MAPPED_LABEL = 'Not mapped';

/** The key a requirement's module goes by in maps and the rail: its id, or `NOT_MAPPED_VIEW`. */
export function moduleKeyOf(row: Pick<EngagementRequirement, 'module_id'>): string {
  return row.module_id ?? NOT_MAPPED_VIEW;
}

/** The module's name, or "Not mapped". */
export function moduleLabelOf(row: Pick<EngagementRequirement, 'module_name'>): string {
  return row.module_name ?? NOT_MAPPED_LABEL;
}

/** How the grid groups its rows: by library module and sub-category, or by the client's own area. */
export type GroupBy = 'module' | 'client_area';

export const GROUP_BY_OPTIONS: ReadonlyArray<{ value: GroupBy; label: string }> = [
  { value: 'module', label: 'Module' },
  { value: 'client_area', label: 'Client area' },
];

export const NO_CLIENT_AREA = 'No client area';

export const CRITICALITY_FILTERS: ReadonlyArray<{ value: CriticalityFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'not_set', label: 'Not set' },
  { value: 'critical', label: 'Critical' },
  { value: 'desired', label: 'Desired' },
  { value: 'not_required', label: 'Not required' },
  { value: 'not_applicable', label: 'N/A' },
];

export const SOURCE_FILTERS: ReadonlyArray<{ value: SourceFilter; label: string }> = [
  { value: 'any', label: 'Any source' },
  { value: 'library', label: 'Library' },
  { value: 'spreadsheet', label: 'Imported' },
  { value: 'manual', label: 'Manual' },
  { value: 'discovery', label: 'Discovery' },
  { value: 'edited', label: 'Edited' },
];

export function parseCriticalityFilter(value: unknown): CriticalityFilter {
  return CRITICALITY_FILTERS.some((f) => f.value === value) ? (value as CriticalityFilter) : 'all';
}

export function parseSourceFilter(value: unknown): SourceFilter {
  return SOURCE_FILTERS.some((f) => f.value === value) ? (value as SourceFilter) : 'any';
}

export interface RequirementFilters {
  query: string;
  criticality: CriticalityFilter;
  source: SourceFilter;
  /** A module id, `NOT_MAPPED_VIEW`, `REMOVED_VIEW`, or null for all requirements. */
  view: string | null;
  /** Only the rows one load brought in (T-0264: what a library match apply added). */
  load?: string | null;
}

/**
 * The table's search: every term must appear in the number, the client
 * reference, the module name ("Not mapped" for none), the client area, the
 * sub-category or the text. The list and export endpoints apply the same
 * rule to `q` (T-0154), so "Export current view" holds exactly the rows
 * shown.
 */
function matchesText(row: EngagementRequirement, terms: ReadonlyArray<string>): boolean {
  if (terms.length === 0) return true;
  const clientRef = (str(obj(row.citation)['client_ref']) ?? '').toLowerCase();
  const area = (row.client_area ?? '').toLowerCase();
  const searchable = {
    number: row.number,
    v1_number: null,
    module_name: moduleLabelOf(row),
    subcategory: row.subcategory,
    text: row.text,
  };
  return terms.every(
    (term) =>
      (clientRef !== '' && clientRef.includes(term)) ||
      (area !== '' && area.includes(term)) ||
      matchesSearch(searchable, [term]),
  );
}

function matchesSource(row: EngagementRequirement, source: SourceFilter): boolean {
  if (source === 'any') return true;
  if (source === 'edited') return row.is_edited;
  // Both kinds from discovery: a library match is also a library requirement.
  if (source === 'discovery') return citesDiscovery(row);
  return citationOf(row).kind === source;
}

const NUMBER_ORDER = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

/**
 * Where a requirement sits in the client's spreadsheet: its file, sheet and
 * row, from the citation of a spreadsheet import or a library match. Null
 * for a requirement from the library picker, written by hand, or from
 * discovery.
 */
export function sourcePlaceOf(
  row: Pick<EngagementRequirement, 'citation'>,
): { file: string; sheet: string; row: number } | null {
  const c = obj(row.citation);
  const file = str(c['file_name']) ?? str(c['file']);
  const at = c['row'];
  if (!file || typeof at !== 'number') return null;
  return { file, sheet: str(c['sheet']) ?? '', row: at };
}

/** Spreadsheet order: file, sheet, then row; rows from no spreadsheet after, by number. */
function compareSource(a: EngagementRequirement, b: EngagementRequirement): number {
  const pa = sourcePlaceOf(a);
  const pb = sourcePlaceOf(b);
  if (pa && pb) {
    return (
      pa.file.localeCompare(pb.file) ||
      NUMBER_ORDER.compare(pa.sheet, pb.sheet) ||
      pa.row - pb.row ||
      NUMBER_ORDER.compare(a.number, b.number)
    );
  }
  if (pa || pb) return pa ? -1 : 1;
  return NUMBER_ORDER.compare(a.number, b.number);
}

/**
 * Order rows for the table.
 *
 * By module: modules in library order (unknown modules last, by name, then
 * Not mapped), then each (module, sub-category) group contiguous in order
 * of its lowest number, rows by number inside a group. Library numbers
 * ("AP-006") sort before client-series numbers ("AP-C01").
 *
 * By client area: the client's areas in the order its spreadsheet first
 * lists them, then "No client area"; inside an area, the spreadsheet's own
 * order (file, sheet, row).
 */
export function orderRows(
  rows: ReadonlyArray<EngagementRequirement>,
  moduleOrder: ReadonlyMap<string, number>,
  groupBy: GroupBy = 'module',
): EngagementRequirement[] {
  const sorted =
    groupBy === 'client_area'
      ? rows.slice().sort(compareSource)
      : rows.slice().sort((a, b) => {
          if ((a.module_id === null) !== (b.module_id === null)) return a.module_id ? -1 : 1;
          const at = (id: string | null) =>
            (id === null ? undefined : moduleOrder.get(id)) ?? Number.MAX_SAFE_INTEGER;
          const ma = at(a.module_id);
          const mb = at(b.module_id);
          if (ma !== mb) return ma - mb;
          if (a.module_id !== b.module_id) {
            return moduleLabelOf(a).localeCompare(moduleLabelOf(b));
          }
          return NUMBER_ORDER.compare(a.number, b.number);
        });
  const groups = new Map<string, EngagementRequirement[]>();
  for (const row of sorted) {
    const key = groupKeyOf(row, groupBy);
    const bucket = groups.get(key);
    if (bucket) bucket.push(row);
    else groups.set(key, [row]);
  }
  if (groupBy === 'client_area') {
    // "No client area" last, whatever its rows' places.
    const none = groupKeyOf({ module_id: null, subcategory: '', client_area: null }, groupBy);
    const last = groups.get(none);
    if (last) {
      groups.delete(none);
      groups.set(none, last);
    }
  }
  return Array.from(groups.values()).flat();
}

/**
 * Group key for the grid: one group per module (or Not mapped) and
 * sub-category, or one per client area. `parseGroupKey` reads it back.
 */
export function groupKeyOf(
  row: Pick<EngagementRequirement, 'module_id' | 'subcategory' | 'client_area'>,
  groupBy: GroupBy = 'module',
): string {
  if (groupBy === 'client_area') {
    return `area\u0000${(row.client_area ?? '').trim().toLowerCase()}`;
  }
  return `${moduleKeyOf(row)}\u0000${row.subcategory}`;
}

export interface RequirementsView {
  terms: string[];
  /** Every active requirement, unfiltered. */
  live: EngagementRequirement[];
  /** Every removed requirement, unfiltered. */
  removed: EngagementRequirement[];
  /** The pool for the current view after search and filters, before the module choice. */
  base: EngagementRequirement[];
  /** Matching active requirements per module id (`NOT_MAPPED_VIEW` for none), for the rail. */
  counts: Map<string, number>;
  /** What the table shows. */
  rows: EngagementRequirement[];
  /** True when search or a filter is narrowing the view. */
  narrowed: boolean;
}

/**
 * Everything the screen derives from the full list and the current
 * filters. Rows must already be in table order (`orderRows`).
 */
export function computeView(
  all: ReadonlyArray<EngagementRequirement>,
  filters: RequirementFilters,
): RequirementsView {
  const terms = searchTerms(filters.query);
  const live = all.filter((r) => r.status !== 'removed');
  const removed = all.filter((r) => r.status === 'removed');
  const removedView = filters.view === REMOVED_VIEW;
  const passes = (r: EngagementRequirement) =>
    (!filters.load || r.load_id === filters.load) &&
    matchesText(r, terms) &&
    (filters.criticality === 'all' || r.criticality === filters.criticality) &&
    matchesSource(r, filters.source);
  const base = (removedView ? removed : live).filter(passes);
  const counts = new Map<string, number>();
  // In the Removed view the rail still counts active requirements.
  for (const r of removedView ? live.filter(passes) : base) {
    const key = moduleKeyOf(r);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const rows =
    filters.view && !removedView ? base.filter((r) => moduleKeyOf(r) === filters.view) : base;
  return {
    terms,
    live,
    removed,
    base,
    counts,
    rows,
    narrowed:
      terms.length > 0 ||
      filters.criticality !== 'all' ||
      filters.source !== 'any' ||
      Boolean(filters.load),
  };
}

export interface HeaderSummary {
  total: number;
  modules: number;
  /** Requirements with no library module. */
  notMapped: number;
  critical: number;
  notSet: number;
}

/** Header figures: always over every active requirement, never the filtered view. */
export function headerSummary(live: ReadonlyArray<EngagementRequirement>): HeaderSummary {
  return {
    total: live.length,
    modules: new Set(live.flatMap((r) => (r.module_id ? [r.module_id] : []))).size,
    notMapped: live.filter((r) => r.module_id === null).length,
    critical: live.filter((r) => r.criticality === 'critical').length,
    notSet: live.filter((r) => r.criticality === 'not_set').length,
  };
}

export function plural(n: number, noun: string, pluralNoun = `${noun}s`): string {
  return `${n.toLocaleString()} ${n === 1 ? noun : pluralNoun}`;
}

export function headerSummaryText(s: HeaderSummary): string {
  if (s.total === 0) return 'No requirements yet';
  return [
    plural(s.total, 'requirement'),
    plural(s.modules, 'module'),
    ...(s.notMapped > 0 ? [`${s.notMapped.toLocaleString()} not mapped`] : []),
    `${s.critical.toLocaleString()} critical`,
    `${s.notSet.toLocaleString()} criticality not set`,
  ].join(' · ');
}

export interface RailModule {
  id: string;
  name: string;
  count: number;
}
export interface RailCategory {
  id: string;
  name: string;
  count: number;
  modules: RailModule[];
}

const OTHER_CATEGORY = '__other';

/**
 * The left rail: only modules the engagement has active requirements in,
 * under their library category, with the count of matching requirements.
 * A module no longer in the library tree is listed under "Other". Not
 * mapped requirements are the rail's own item (`NOT_MAPPED_VIEW`), not a
 * module.
 */
export function railCategories(
  live: ReadonlyArray<EngagementRequirement>,
  counts: ReadonlyMap<string, number>,
  tree: LibraryTree | undefined,
): RailCategory[] {
  const present = new Map<string, string>();
  for (const r of live) {
    if (r.module_id && r.module_name && !present.has(r.module_id)) {
      present.set(r.module_id, r.module_name);
    }
  }
  const out: RailCategory[] = [];
  const placed = new Set<string>();
  for (const c of tree?.categories ?? []) {
    const modules = c.modules
      .filter((m) => present.has(m.id))
      .map((m) => ({ id: m.id, name: present.get(m.id) ?? m.name, count: counts.get(m.id) ?? 0 }));
    if (modules.length === 0) continue;
    modules.forEach((m) => placed.add(m.id));
    out.push({
      id: c.id,
      name: c.name,
      count: modules.reduce((n, m) => n + m.count, 0),
      modules,
    });
  }
  const rest = Array.from(present.entries())
    .filter(([id]) => !placed.has(id))
    .map(([id, name]) => ({ id, name, count: counts.get(id) ?? 0 }))
    .sort((a, b) => a.name.localeCompare(b.name));
  if (rest.length > 0) {
    out.push({
      id: OTHER_CATEGORY,
      name: tree ? 'Other' : 'Modules',
      count: rest.reduce((n, m) => n + m.count, 0),
      modules: rest,
    });
  }
  return out;
}

/** Module id -> position in the library tree, for `orderRows`. */
export function moduleOrderOf(tree: LibraryTree | undefined): Map<string, number> {
  const order = new Map<string, number>();
  let i = 0;
  for (const c of tree?.categories ?? []) for (const m of c.modules) order.set(m.id, i++);
  return order;
}

// ---------------------------------------------------------------------------
// Wording compared with the library
// ---------------------------------------------------------------------------

function normaliseWording(text: string): string {
  return text.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * The library wording a loaded requirement has drifted from, or null.
 *
 * Only reported when the library item is still at the version the row was
 * loaded from: then a difference means the consultant reworded it. When
 * the library has moved on, the engagement's copy is deliberately left
 * alone (E11-F05 scenario 5) and nothing is flagged.
 */
export function libraryWordingDifference(
  row: EngagementRequirement,
  libraryById: ReadonlyMap<string, LibraryItem>,
): string | null {
  if (!row.library_item_id) return null;
  const item = libraryById.get(row.library_item_id);
  if (!item) return null;
  if (row.library_module_version != null && item.version !== row.library_module_version) {
    return null;
  }
  return normaliseWording(item.text) === normaliseWording(row.text) ? null : item.text;
}

// ---------------------------------------------------------------------------
// Bulk undo
// ---------------------------------------------------------------------------

/**
 * The calls that put a bulk criticality change back: one per distinct
 * prior value, since the rows may not all have had the same one.
 */
export function criticalityUndoPlan(
  previous: ReadonlyArray<{ id: string; criticality: string }>,
): Array<{ criticality: Criticality; ids: string[] }> {
  const by = new Map<Criticality, string[]>();
  for (const p of previous) {
    const c = asCriticality(p.criticality);
    const ids = by.get(c);
    if (ids) ids.push(p.id);
    else by.set(c, [p.id]);
  }
  return Array.from(by.entries()).map(([criticality, ids]) => ({ criticality, ids }));
}

// ---------------------------------------------------------------------------
// Add-from-library picker
// ---------------------------------------------------------------------------

export type PickState = 'on' | 'off' | 'mixed' | 'locked';

export interface EngagementLibraryState {
  /** Library item ids the engagement holds as active requirements. */
  present: Set<string>;
  /** Library item ids the engagement holds as removed requirements. */
  removed: Set<string>;
}

/** Which library items the engagement already holds, by lineage (never by wording). */
export function engagementLibraryState(
  rows: ReadonlyArray<EngagementRequirement>,
): EngagementLibraryState {
  const present = new Set<string>();
  const removed = new Set<string>();
  for (const r of rows) {
    if (!r.library_item_id) continue;
    if (r.status === 'removed') removed.add(r.library_item_id);
    else present.add(r.library_item_id);
  }
  return { present, removed };
}

type PickItem = Pick<LibraryItem, 'item_id'>;

/**
 * Checkbox state for a set of library items: locked when every one is
 * already in the engagement, otherwise on / off / mixed over the ones
 * that can still be picked.
 */
export function pickState(
  items: ReadonlyArray<PickItem>,
  present: ReadonlySet<string>,
  picked: ReadonlySet<string>,
): PickState {
  const open = items.filter((i) => !present.has(i.item_id));
  if (open.length === 0) return 'locked';
  const n = open.filter((i) => picked.has(i.item_id)).length;
  return n === 0 ? 'off' : n === open.length ? 'on' : 'mixed';
}

/**
 * Toggle a set of items: if every pickable one is picked, clear them;
 * otherwise pick them all. Items already in the engagement never change.
 */
export function togglePick(
  picked: ReadonlySet<string>,
  items: ReadonlyArray<PickItem>,
  present: ReadonlySet<string>,
): Set<string> {
  const next = new Set(picked);
  const open = items.filter((i) => !present.has(i.item_id));
  const allOn = open.length > 0 && open.every((i) => next.has(i.item_id));
  for (const i of open) {
    if (allOn) next.delete(i.item_id);
    else next.add(i.item_id);
  }
  return next;
}

export interface PickSummary {
  /** Picked items the engagement has never held. */
  toAdd: string[];
  /** Picked items the engagement removed earlier. */
  removedEarlier: string[];
  /** Items in the modules being picked from that are already in the engagement. */
  alreadyPresent: string[];
  /** What confirming will bring in: to add, plus removed earlier when restoring. */
  total: number;
}

/**
 * The running totals in the picker footer, in the same three groups the
 * server's `:load-preview` reports. "Already present" counts the held
 * items of every module the consultant has ticked something in, so
 * ticking a module that was loaded before shows "0 to add, N already in
 * this engagement" (E11-F05 scenario 3).
 */
export function pickSummary(
  items: ReadonlyArray<Pick<LibraryItem, 'item_id' | 'module_id'>>,
  picked: ReadonlySet<string>,
  touchedModules: ReadonlySet<string>,
  state: EngagementLibraryState,
  restoreRemoved: boolean,
): PickSummary {
  const toAdd: string[] = [];
  const removedEarlier: string[] = [];
  const alreadyPresent: string[] = [];
  const modules = new Set(touchedModules);
  for (const i of items) if (picked.has(i.item_id)) modules.add(i.module_id);
  for (const i of items) {
    if (state.present.has(i.item_id)) {
      if (modules.has(i.module_id)) alreadyPresent.push(i.item_id);
    } else if (picked.has(i.item_id)) {
      if (state.removed.has(i.item_id)) removedEarlier.push(i.item_id);
      else toAdd.push(i.item_id);
    }
  }
  return {
    toAdd,
    removedEarlier,
    alreadyPresent,
    total: toAdd.length + (restoreRemoved ? removedEarlier.length : 0),
  };
}

/** The toast after a load, from the server's own counts. */
export function loadResultMessage(result: {
  added: number;
  restored: number;
  skipped_detail: { already_present: number };
}): string {
  const parts: string[] = [];
  if (result.added > 0 || result.restored === 0) {
    parts.push(`${plural(result.added, 'requirement')} added from the library`);
  }
  if (result.restored > 0) {
    parts.push(
      parts.length > 0
        ? `${result.restored.toLocaleString()} brought back`
        : `${plural(result.restored, 'requirement')} brought back`,
    );
  }
  const already = result.skipped_detail.already_present;
  return `${parts.join(', ')}${already > 0 ? `; ${already.toLocaleString()} ${already === 1 ? 'was' : 'were'} already in this engagement` : ''}.`;
}

/** "4 Oct 2026", in the reader's time zone. Empty for a missing date. */
export { formatDate } from '@/components/requirement-library/library-logic';

// ---------------------------------------------------------------------------
// Spreadsheet import and export (T-0153)
// ---------------------------------------------------------------------------

/** The toast after a spreadsheet import, from the server's own counts. */
export function importResultMessage(result: {
  added: number;
  file_name: string;
  duplicates_skipped: number;
  excluded: number;
}): string {
  const left: string[] = [];
  if (result.duplicates_skipped > 0) {
    left.push(`${plural(result.duplicates_skipped, 'duplicate')} skipped`);
  }
  if (result.excluded > 0) left.push(`${result.excluded.toLocaleString()} excluded`);
  return `${plural(result.added, 'requirement')} imported from ${result.file_name}${
    left.length > 0 ? `; ${left.join(', ')}` : ''
  }.`;
}

/** The toast after an import is undone, naming the rows that stayed. */
export function undoImportMessage(result: {
  removed: number;
  kept_edited: ReadonlyArray<{ number: string }>;
}): string {
  const removed = `Import undone: ${plural(result.removed, 'requirement')} removed.`;
  if (result.kept_edited.length === 0) return removed;
  const shown = result.kept_edited.slice(0, 5).map((r) => r.number);
  const more = result.kept_edited.length - shown.length;
  return `${removed} ${plural(result.kept_edited.length, 'requirement')} edited since the import ${
    result.kept_edited.length === 1 ? 'was' : 'were'
  } kept: ${shown.join(', ')}${more > 0 ? ` and ${more.toLocaleString()} more` : ''}.`;
}

export interface ExportOptions {
  /** One tab per module instead of a single sheet. */
  perModule: boolean;
  includeRemoved: boolean;
}

export interface ExportQuery {
  module_id?: string;
  criticality?: string;
  source_kind?: string;
  edited?: 'true';
  status?: string;
  q?: string;
  sheets?: 'per_module';
  include_removed?: 'true';
  load_id?: string;
}

/**
 * The query string of an export. `filters` null exports every requirement;
 * otherwise the file holds what the table shows: the selected module (or
 * the Removed view), both filters and the search text, in the list
 * endpoint's own parameter names.
 */
export function exportQuery(
  filters: RequirementFilters | null,
  options: ExportOptions,
): ExportQuery {
  const query: ExportQuery = {};
  if (filters) {
    if (filters.view === REMOVED_VIEW) query.status = 'removed';
    else if (filters.view) query.module_id = filters.view; // a module id, or `not_mapped`
    if (filters.criticality !== 'all') query.criticality = filters.criticality;
    if (filters.source === 'edited') query.edited = 'true';
    else if (filters.source !== 'any') query.source_kind = filters.source;
    const q = filters.query.trim();
    if (q) query.q = q;
    if (filters.load) query.load_id = filters.load;
  }
  if (options.perModule) query.sheets = 'per_module';
  // An explicit status filter wins on the server; do not send both.
  if (options.includeRemoved && !query.status) query.include_removed = 'true';
  return query;
}
